import axios, { AxiosInstance, AxiosResponse } from "axios";
import { ERP_BASE_URL, MEDIA_BASE_URL } from "../../config/apiConfig";

const getHeaderValue = (headerValue: unknown, fallback = "") => {
	if (typeof headerValue === "string") {
		return headerValue;
	}

	if (Array.isArray(headerValue)) {
		return headerValue.join(", ");
	}

	return fallback;
};

export interface UIDPartTicketMapping {
	ticketId: number;
	uid: string | null;
	uidStatus: string;
	fleetName: string;
	fleetType: string;
	fleetStatus?: string;
	createdAt: string;
	component: string;
	partState: string;
}

export interface PartItem {
	id: number;
	referenceCode: string;
	description: string;
}

export interface WarehouseItem {
	id: number;
	name: string;
	description?: string;
}

export interface WarehouseListResponse {
	code: number;
	message: string;
	data: {
		pageNumber: number;
		totalPages: number;
		data: WarehouseItem[];
	};
}

export interface PartsListResponse {
	code: number;
	message: string;
	data: {
		pageNumber: number;
		totalPages: number;
		data: PartItem[];
	};
}

export interface UIDPartTicketDetails {
	referenceCode?: string;
	partCode?: string;
	partId?: number;
	partName?: string;
	onBoardingDate?: string;
	expiryDate?: string;
	hubName?: string;
	fleetStatus?: string;
	raisedBy?: string;
	oemName?: string;
	uidImageId?: number;
}

export interface UIDPartTicketDetailsResponse {
	code: number;
	message: string;
	data: UIDPartTicketDetails;
}

export interface UIDPartTicketListResponse {
	code: number;
	message: string;
	data: {
		partMapTickets: UIDPartTicketMapping[];
		totalPages: number;
		isLast: boolean;
	};
}

export class UIDPartApi {
	public static api = new UIDPartApi();
	private instance: AxiosInstance;

	constructor() {
		this.instance = axios.create({
			baseURL: ERP_BASE_URL,
			headers: {
				"Content-Type": "application/json",
			},
		});
	}

	private getToken() {
		return localStorage.getItem("token");
	}

	async getUIDPartTicketMappings(
		page?: number,
		size?: number,
		search?: string,
		component?: string,
		warehouseId?: number,
		status?: string
	): Promise<UIDPartTicketListResponse> {
		try {
			const response: AxiosResponse<UIDPartTicketListResponse> =
				await this.instance.get("/erp/api/v1/part/map/ticket", {
					headers: {
						Authorization: `Bearer ${this.getToken()}`,
					},
					params: {
						page,
						size,
						search,
						...(component && { component }),
						...(warehouseId && { warehouseId }),
						...(status && { status }),
					},
				});
			return response.data;
		} catch (error) {
			console.error("Error fetching UID-Part ticket mappings", error);
			throw error;
		}
	}

	async downloadUIDPartTicketMappings(
		search?: string,
		component?: string,
		warehouseId?: number,
		status?: string
	): Promise<void> {
		try {
			const response: AxiosResponse<Blob> = await this.instance.get(
				"/erp/api/v1/part/map/ticket",
				{
					headers: {
						Authorization: `Bearer ${this.getToken()}`,
						Accept:
							"text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream",
					},
					params: {
						search,
						...(component && { component }),
						...(warehouseId && { warehouseId }),
						...(status && { status }),
						download: true,
					},
					responseType: "blob",
				}
			);
			this.downloadFile(response, "uid-part-mapping-report");
		} catch (error) {
			console.error("Error downloading UID-Part ticket mappings", error);
			throw error;
		}
	}

	async getUIDPartTicketDetails(
		ticketId: number
	): Promise<UIDPartTicketDetailsResponse> {
		try {
			const response: AxiosResponse<UIDPartTicketDetailsResponse> =
				await this.instance.get("/erp/api/v1/part/map/ticket/details", {
					headers: {
						Authorization: `Bearer ${this.getToken()}`,
					},
					params: {
						ticketId,
					},
				});
			return response.data;
		} catch (error) {
			console.error("Error fetching UID-Part ticket details", error);
			throw error;
		}
	}

	async getPartsList(
		page?: number,
		pageSize?: number,
		partTypeId?: number,
		search?: string  // ✅ NEW: search param
	): Promise<PartsListResponse> {
		try {
			const response: AxiosResponse<PartsListResponse> =
				await this.instance.get("/erp/api/v1/parts/all", {
					headers: {
						Authorization: `Bearer ${this.getToken()}`,
					},
					params: {
						page,
						pageSize,
						...(partTypeId && { partTypeId }),
						...(search && { search }), // ✅ only sent when non-empty
					},
				});
			return response.data;
		} catch (error) {
			console.error("Error fetching parts list", error);
			throw error;
		}
	}

	async mapPartToTicket(
		ticketId: number,
		partId: number,
		onBoardingDate: string,
		expiryDate: string
	): Promise<{ code: number; message: string; data: any }> {
		try {
			const response: AxiosResponse<{ code: number; message: string; data: any }> =
				await this.instance.put("/erp/api/v1/part/map/ticket", {
					ticketId,
					partMapTicketStatus: "PART_MAPPED",
					partMapTicketData: {
						partId,
						onBoardingDate,
						expiryDate,
					},
				},
					{
						headers: {
							Authorization: `Bearer ${this.getToken()}`,
						},
					});
			return response.data;
		} catch (error) {
			console.error("Error mapping part to ticket", error);
			throw error;
		}
	}

	async cancelPartMapTicket(
		ticketId: number,
		partId: number,
		onBoardingDate: string,
		expiryDate: string
	): Promise<{ code: number; message: string; data: any }> {
		try {
			const response: AxiosResponse<{ code: number; message: string; data: any }> =
				await this.instance.put("/erp/api/v1/part/map/ticket", {
					ticketId,
					partMapTicketStatus: "CANCELLED",
					partMapTicketData: {
						partId,
						onBoardingDate,
						expiryDate,
					},
				},
					{
						headers: {
							Authorization: `Bearer ${this.getToken()}`,
						},
					});
			return response.data;
		} catch (error) {
			console.error("Error cancelling part map ticket", error);
			throw error;
		}
	}

	async getWarehousesList(
		page?: number,
		pageSize?: number
	): Promise<WarehouseListResponse> {
		try {
			const response: AxiosResponse<WarehouseListResponse> =
				await this.instance.get("/erp/api/v1/warehouse/all", {
					headers: {
						Authorization: `Bearer ${this.getToken()}`,
					},
					params: {
						page,
						pageSize,
					},
				});
			return response.data;
		} catch (error) {
			console.error("Error fetching warehouse list", error);
			throw error;
		}
	}

	async getBulkUploadHistory(
		page?: number,
		pageSize?: number,
		purpose?:string,
		
	): Promise<any> {
		try {
			const response: AxiosResponse<any> =
				await this.instance.get("/erp/api/v1/bulk-upload/history", {
					headers: {
						Authorization: `Bearer ${this.getToken()}`,
					},
					params:{
						page,
						pageSize,
						purpose
					}
				});
			return response.data;
		} catch (error) {
			console.error("Error fetching bulk upload history", error);
			throw error;
		}
	}

	async uploadFile(file: File): Promise<{
		fileId?: number;
		fileName?: string;
		data?: { fileId: number; fileName: string };
	}> {
		try {
			const formData = new FormData();
			formData.append("file", file);

			const response = await axios.post(`${MEDIA_BASE_URL}/upload`, formData, {
				headers: {
					Authorization: `Bearer ${this.getToken()}`,
					"Content-Type": "multipart/form-data",
				},
			});

			return response.data;
		} catch (error) {
			console.error("Error uploading file", error);
			throw error;
		}
	}

	async bulkPartMappingUpload(
		fileId: number,
		fileName: string
	): Promise<any> {
		try {
			const response = await this.instance.put(
				"/erp/api/v1/part/map/ticket/bulk",
				{},
				{
					headers: {
						Authorization: `Bearer ${this.getToken()}`,
					},
					params: {
						fileId,
						fileName,
					},
				}
			);

			return response.data;
		} catch (error) {
			console.error("Error uploading bulk part mapping", error);
			throw error;
		}
	}

	private downloadFile(response: AxiosResponse<Blob>, defaultFileName: string) {
		try {
			if (!response.data || response.data.size === 0) {
				throw new Error("Empty file received");
			}

			const contentType = getHeaderValue(
				response.headers["content-type"],
				"application/octet-stream"
			);
			let fileExtension = ".xlsx";

			if (contentType.includes("text/csv")) {
				fileExtension = ".csv";
			} else if (contentType.includes("application/pdf")) {
				fileExtension = ".pdf";
			} else if (contentType.includes("spreadsheetml")) {
				fileExtension = ".xlsx";
			}

			const blob = new Blob([response.data], { type: contentType });

			const contentDisposition = getHeaderValue(
				response.headers["content-disposition"]
			);
			let filename = `${defaultFileName}-${new Date().toISOString().split("T")[0]
				}${fileExtension}`;

			if (contentDisposition) {
				const filenameMatch = contentDisposition.match(
					/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
				);
				if (filenameMatch && filenameMatch[1]) {
					filename = filenameMatch[1].replace(/['"]/g, "");
				} else {
					const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.+)/);
					if (utf8Match && utf8Match[1]) {
						filename = decodeURIComponent(utf8Match[1]);
					}
				}
			}

			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = filename;
			link.style.display = "none";
			document.body.appendChild(link);
			link.click();

			setTimeout(() => {
				if (document.body.contains(link)) {
					document.body.removeChild(link);
				}
				window.URL.revokeObjectURL(url);
			}, 100);
		} catch (error) {
			console.error("Error in downloadFile:", error);
			throw new Error("Failed to download file. Please try again.");
		}
	}
}
